// The bell — what is waiting on me, from anywhere in the product
// apps/smart-catalog/frontend/src/components/notifications/NotificationBell.tsx
//
// Reminders and escalations from every registered source land here. A row is
// the title, one line of detail and how long ago; clicking opens the thing and
// marks it read. Escalations carry a small mark so they stand out without
// shouting.

import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import {
  Badge,
  Box,
  ButtonBase,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import notificationService, {
  type NotificationV1,
} from "../../services/notificationService";

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

export const NotificationBell: React.FC<{
  iconColor?: string;
  hoverSx?: object;
}> = ({ iconColor, hoverSx }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data } = useQuery({
    queryKey: ["notifications", "mine"],
    queryFn: notificationService.mine,
    refetchInterval: 60_000,
    // A missing session or a backend that is down should leave a quiet bell,
    // not an error in the top bar.
    retry: false,
  });
  const read = useMutation({
    mutationFn: (ids?: string[]) => notificationService.markRead(ids),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications", "mine"] }),
  });
  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  const open = (n: NotificationV1) => {
    if (!n.readAt) read.mutate([n.notificationId]);
    setAnchor(null);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: iconColor, ...hoverSx }}
        >
          <Badge
            color="error"
            badgeContent={unread}
            invisible={!unread}
            max={99}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxHeight: 520,
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", flexGrow: 1 }}>
            Notifications
          </Typography>
          {unread > 0 && (
            <ButtonBase
              onClick={() => read.mutate(undefined)}
              sx={{ fontSize: "0.75rem", color: "primary.main" }}
            >
              Mark all read
            </ButtonBase>
          )}
        </Stack>
        <Box sx={{ overflowY: "auto" }}>
          {items.length === 0 ? (
            <Typography
              sx={{
                px: 2,
                py: 4,
                textAlign: "center",
                fontSize: "0.85rem",
                color: "text.secondary",
              }}
            >
              Nothing is waiting on you.
            </Typography>
          ) : (
            items.map((n) => (
              <ButtonBase
                key={n.notificationId}
                onClick={() => open(n)}
                sx={{
                  display: "flex",
                  width: "100%",
                  textAlign: "left",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: (t) =>
                    n.readAt
                      ? "transparent"
                      : alpha(t.palette.primary.main, 0.04),
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    mt: 0.75,
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: n.readAt
                      ? "transparent"
                      : n.kind === "ESCALATION"
                        ? "warning.main"
                        : "primary.main",
                  }}
                />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.84rem",
                      fontWeight: n.readAt ? 500 : 600,
                    }}
                  >
                    {n.title}
                  </Typography>
                  {n.body && (
                    <Typography
                      noWrap
                      sx={{ fontSize: "0.78rem", color: "text.secondary" }}
                    >
                      {n.body.split("\n")[0]}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.disabled",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ago(n.createdAt)}
                </Typography>
              </ButtonBase>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
